import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import Decimal from 'decimal.js';
import { HtmlTable } from '../../../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { IconButton } from '../../../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../../../../../../../CommonComponents/OPL/Button/Button';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';
import { FormValues } from '../../../../UpsertTrustModal.meta';
import { BeneficiaryType } from '../../../../../../../../api/useTrustsQuery';
import { MarginProps } from '../../../../../../../../../../theme/styledProps';
import { formatNumber } from '../../../../../../../../../../CommonUtils/formatNumber';
import { BeneficiaryFieldArray } from '../../hooks/useBeneficiaryFieldArray';
import { HelperText } from '../../../../../../../../../../CommonComponents/OPL/HelperText/HelperText';

interface Props extends MarginProps {
  beneficiaryType: BeneficiaryType;
  fieldArray: BeneficiaryFieldArray;
  beneficiariesOptions: { value: string; label: string }[];
}

export const BeneficiariesTable: React.FC<Props> = ({
  beneficiaryType,
  fieldArray: { fields, append, remove },
  beneficiariesOptions,
  ...rest
}) => {
  const fieldName =
    beneficiaryType === BeneficiaryType.Primary
      ? 'beneficiaries.primary'
      : 'beneficiaries.contingent';

  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const totalAllocation = fields.reduce((acc, { percentage }) => {
    if (percentage) {
      return new Decimal(acc).plus(percentage).toNumber();
    }

    return acc;
  }, 0);

  return (
    <Box as="section" {...rest} mt={-1}>
      <HtmlTable variant="embedded" edgePadding={24}>
        <table>
          <colgroup>
            <col width="80%" />
            <col width="20%" />
            <col width="0%" />
          </colgroup>

          <thead>
            <tr>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  {beneficiaryType === BeneficiaryType.Primary ? 'Primary' : 'Contingent'}
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Allocation
                </Text>
              </th>

              <th>
                <Box width={36} />
              </th>
            </tr>
          </thead>

          <tbody>
            {fields.map((beneficiary, index) => {
              return (
                <tr key={beneficiary.key}>
                  <td>
                    <input type="hidden" {...register(`${fieldName}.${index}.id`)} />
                    <input type="hidden" {...register(`${fieldName}.${index}.type`)} />

                    <Controller
                      control={control}
                      name={`${fieldName}.${index}.name`}
                      render={({ field: { ref } }) => (
                        <Select
                          isCreatable
                          innerRef={ref}
                          value={
                            beneficiary.name
                              ? {
                                  label: beneficiary.name,
                                  value: beneficiary.id,
                                }
                              : null
                          }
                          onChange={opt => {
                            if (opt) {
                              const isNew = get(opt, '__isNew__', false);
                              const id = isNew ? null : opt.value;

                              setValue(`${fieldName}.${index}.name`, opt.label, {
                                shouldValidate: true,
                              });
                              setValue(`${fieldName}.${index}.id`, id);
                            }
                          }}
                          options={beneficiariesOptions}
                          formatCreateLabel={value => `Add ${value}`}
                          noOptionsMessage={() => 'Please type a beneficiary'}
                          formVariant={
                            errors.beneficiaries?.primary?.[index]?.name ? 'error' : undefined
                          }
                          placeholder="Select Beneficiary..."
                          aria-label="Beneficiary name"
                          isPortal
                        />
                      )}
                    />
                  </td>

                  <td>
                    <InputNumberMask
                      {...register(`${fieldName}.${index}.percentage`)}
                      value={beneficiary.percentage}
                      allowNegative={false}
                      thousandSeparator={false}
                      decimalScale={2}
                      rightIcon="far fa-percent"
                      placeholder="0"
                      formVariant={
                        errors.beneficiaries?.primary?.[index]?.percentage ? 'error' : undefined
                      }
                      aria-label="Beneficiary allocation percentage"
                    />
                  </td>

                  <td>
                    <IconButton
                      icon="far fa-minus-circle"
                      onClick={() => remove(index)}
                      variant="subtleInactive"
                      aria-label="Remove beneficiary"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </HtmlTable>

      <Button
        leftIcon="fas fa-plus"
        variant="link"
        onClick={() =>
          append({
            id: '',
            name: '',
            type: beneficiaryType,
            percentage: '',
          })
        }
        my="16"
        ml="24"
      >
        Add a Beneficiary
      </Button>

      {fields.length > 0 && (
        <Box
          as="footer"
          display="flex"
          alignItems="center"
          borderStyle="solid"
          borderColor="neutral.200"
          borderWidth="1px 0"
          py="8"
        >
          <Text as="p" fontWeight={600} css="flex-basis: 396px; text-align: right">
            Total Allocation
          </Text>

          <Box flex={1} ml="24">
            <Text as="p" fontWeight={600}>
              {formatNumber(totalAllocation, {
                decimalPlaces: 2,
                thousandSeparated: false,
                optionalMantissa: true,
              })}
              %
            </Text>

            {totalAllocation !== 100 && (
              <HelperText variant="error">The total must be 100%</HelperText>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
